import { useDashboardStats } from './hooks/useDashboardStats';
// Import Lucide icons
import { BarChart2, Users, Briefcase, TrendingUp } from 'lucide-react'; 
// Import Shadcn UI components
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton"; // Use Skeleton for loading
import { cn } from "@/lib/utils"; // Import cn utility

interface DashboardStatsProps {
  projectId?: string; // Optional project ID for project-specific stats
}

export function DashboardStats({ projectId }: DashboardStatsProps) {
  const { stats, isLoading } = useDashboardStats(projectId);

  const statCardsConfig = [
    {
      label: projectId ? 'Project Analyses' : 'Total Analyses',
      valueKey: 'totalAnalyses',
      icon: BarChart2,
      iconColor: "text-blue-600",
      gradient: "from-blue-50 to-blue-100/50",
      borderColor: "border-blue-200/50",
      hoverGradient: "hover:from-blue-100 hover:to-blue-200/50",
      shadowColor: "shadow-blue-100/50",
    },
    {
      label: projectId ? 'Project Candidates' : 'Candidates',
      valueKey: 'totalCandidates',
      icon: Users,
      iconColor: "text-emerald-600",
      gradient: "from-emerald-50 to-emerald-100/50",
      borderColor: "border-emerald-200/50",
      hoverGradient: "hover:from-emerald-100 hover:to-emerald-200/50",
      shadowColor: "shadow-emerald-100/50",
    },
    {
      label: projectId ? 'Project Jobs' : 'Job Descriptions',
      valueKey: 'totalJobs',
      icon: Briefcase,
      iconColor: "text-violet-600",
      gradient: "from-violet-50 to-violet-100/50",
      borderColor: "border-violet-200/50",
      hoverGradient: "hover:from-violet-100 hover:to-violet-200/50",
      shadowColor: "shadow-violet-100/50",
    },
  ] as const;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {statCardsConfig.map((stat) => (
          <Card key={stat.label} className="bg-white/90 backdrop-blur-sm border border-slate-200/60 rounded-2xl shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-6 pt-6">
              <Skeleton className="h-5 w-32 rounded-lg" />
              <Skeleton className="h-12 w-12 rounded-2xl" />
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <Skeleton className="h-8 w-20 mt-2 rounded-lg" />
              <Skeleton className="h-3 w-24 mt-3 rounded-lg" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      {statCardsConfig.map((stat) => (
        <Card 
          key={stat.label} 
          className={cn(
            "group cursor-pointer transition-all duration-300 bg-gradient-to-br bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm hover:shadow-lg border",
            stat.gradient,
            stat.borderColor,
            stat.hoverGradient,
            stat.shadowColor,
            "hover:scale-[1.02] transform hover:-translate-y-1"
          )}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-6 pt-6">
            <CardTitle className="text-sm font-semibold text-slate-700 tracking-wide">
              {stat.label}
            </CardTitle>
            <div className={cn(
              "p-3 rounded-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3",
              stat.gradient.replace(/50/g, '100'),
              "shadow-lg border border-white/50"
            )}>
              <stat.icon className={cn("h-6 w-6", stat.iconColor)} /> 
            </div>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="text-3xl font-bold text-slate-900 mb-2 group-hover:text-slate-800 transition-colors">
              {stats ? stats[stat.valueKey]?.toLocaleString() : '-'} 
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span>Active & growing</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}